import { useCallback, useMemo, useRef } from 'react'

export const useDragState = () => {
	// Stores initial drag state (touch offset and sheet height)
	const initialState = useRef({
		offset: 0,
		height: 0,
		startY: 0,
	})

	// Tracks whether global scroll should be locked while dragging to close
	const scrollLockRef = useRef(false)

	// Tracks the current drag distance from the fully opened position
	const dragDistanceRef = useRef(0)

	/**
	 * Resets all drag-related state to initial values
	 * Optionally preserves startY position for gesture continuity
	 */
	const clearDragState = useCallback(
		({ startY = 0, unlockScroll = true } = {}) => {
			initialState.current = {
				offset: 0,
				height: 0,
				startY,
			}
			dragDistanceRef.current = 0
			if (unlockScroll) {
				scrollLockRef.current = false
			}
		},
		[]
	)

	/**
	 * Initializes drag state with touch position and sheet dimensions
	 * Locks scroll to prevent conflicts with native scroll behavior
	 */
	const setDragState = useCallback(
		(offset: number, height: number, startY: number) => {
			initialState.current = { offset, height, startY }
			dragDistanceRef.current = 0
			scrollLockRef.current = true
		},
		[]
	)

	return useMemo(
		() => ({
			initialState,
			scrollLockRef,
			dragDistanceRef,
			clearDragState,
			setDragState,
		}),
		[clearDragState, setDragState]
	)
}
